import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';

import STAR from './images/rating.png';
import { Rate } from 'antd';
import MODEL from './images/calender.png';
import BRAND from './images/brand.png';
import WARRANTY from './images/warranty.png';
import CONDITION from './images/conditions.png';
import HEADERIMAGE from './images/roadBikes.png';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const averageRating = publicData?.averageRating;
  const rating = averageRating ? averageRating * 5 : 0;
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}

      {/*<Rate disabled defaultValue={rating} />*/}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const abbreviatedName = author.attributes.profile.abbreviatedName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

    const getFirstName = authorName => {
      let nameParts = authorName?.split(' ');
      if (nameParts?.length > 1) {
        if (nameParts[0].length === 1) {
          return nameParts[1];
        } else {
          return nameParts[0];
        }
      } else {
        return authorName;
      }
    };

    const providerName = getFirstName(authorName);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

    const valueWarranty = config.listing.listingFields.find(e => e.enumOptions?.find(e => {
      if(e.option == publicData?.Warranty){
        return e.label;
      }
    }));

    const warrantyLabel = valueWarranty?.enumOptions?.map( e => {
      if(e?.option == publicData?.Warranty){
        return e.label;
      }
    }).filter( e => e);

    const valueCondition = config.listing.listingFields.find(e => e.enumOptions?.find(e => {
      if(e.option == publicData?.Condition){
        return e.label;
      }
    }));

    const conditionLabel = valueCondition?.enumOptions?.map( e => {
      if(e?.option == publicData?.Condition){
        return e.label;
      }
    }).filter( e => e);

    const Condition = conditionLabel?.length > 0 && conditionLabel || 'N/A';
    const Warranty = warrantyLabel?.length > 0 && warrantyLabel  || 'N/A';
    const Model_year = publicData?.Modal_Year || 'N/A';
    const category2 = config?.listing?.listingSubCategories?.find( x => x.id == listing?.attributes?.publicData?.categoryLevel2);
    const Brand = category2?.subcategories?.find(x=>x.id== listing?.attributes?.publicData?.categoryLevel3)?.name || 'N/A';
    const subCategory = publicData?.categoryLevel2 || 'N/A';
    const size = publicData?.Size || 'N/A'
    const sellerVerfied = author?.attributes?.profile?.publicData?.isUserVerified || false;
    
  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
       <div className={css.listingItemtags}>
        {sellerVerfied? <div className={css.item2}>
        <FormattedMessage id="ListingCard.verifiedUser" />
        </div> : <div className={css.item}>
        <FormattedMessage id="ListingCard.nonVerifiedUser" />
        </div>}
        <div className={css.item}>
          {subCategory.toUpperCase()}
        </div>
        {Model_year == 'N/A' ? null : 
        <div className={css.item}>
         {Model_year}
        </div>}
        {size == 'N/A' ? null : 
        <div className={css.item}>
          {size.toUpperCase()}
        </div>}

       </div>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <><div className={css.authorInfo}>
              {abbreviatedName?.toUpperCase()}
            </div>
            <div className={css.listingInfoSection}>
              <div className={css.listingInfo}> 
                <div className={css.ListItems}>
                  <img src={BRAND} width={20}/>
                  <div className= {css.authorInfo}>{Brand}</div> 
                </div>
                {/*<div className={css.ListItems}>
                  <img src={MODEL} width={20}/>
                  <div className= {css.authorInfo}>{Model_year}</div>
                </div>*/}
              </div>
              {/*<div className={css.listingInfo}> 
                <div className={css.ListItems}>
                  <img src={CONDITION} width={20}/> 
                  <div className= {css.authorInfo}>{Condition}</div>
                </div>
                <div className={css.ListItems}>
                  <img src={WARRANTY} width={20}/>
                  <div className= {css.authorInfo}>{Warranty}</div>
                </div>
              </div>*/}
            </div>
            </>
          ) : null}
        </div>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
